/**
 * pages/Admin.jsx
 * ----------------
 * Application settings hub — all per-user configuration in one place.
 *
 * Layout: pill tab bar (top) + accordion sections within each tab.
 *
 *   Settings   — Features, Nav Bar, General Settings, Row Colors (all open by default)
 *   Valuation  — Factor settings, Apply Global Valuation, Reset Book Value Timers (flat, short)
 *   Dictionary — Player Dictionary, Value Dictionary (both open by default)
 *   Data       — Card Sets, Card Import, Data Management (Card Sets open by default)
 *
 * Save behavior:
 *   - Most fields: debouncedSave() — 1500ms after last change → PUT /settings/
 *   - Toggles (dark_mode, chatbot, smart_fill): handleToggle() → immediate save
 *   - On save: dispatches "settings-changed" CustomEvent so AppHeader and AuthContext
 *     refresh their state without a page reload.
 */
import React, { useEffect, useRef, useState } from "react";
import api from "../api/api";
import AppHeader from "../components/AppHeader";
import { Link, useNavigate, useLocation } from "react-router-dom";
import ChipsInput from "../components/ChipsInput";
import { useAuth } from "../context/AuthContext";
import "./Admin.css";

const TABS = [
  { id: "settings",   label: "Settings"   },
  { id: "valuation",  label: "Valuation"  },
  { id: "dictionary", label: "Dictionary" },
  { id: "data",       label: "Data"       },
  { id: "users",      label: "Users"      },
];

// Accordion section IDs per tab (Valuation is flat — no entry)
const TAB_SECTIONS = {
  settings:   ["s-features", "s-navbar", "s-variants", "s-general", "s-rowcolors"],
  dictionary: ["d-player", "d-value"],
  data:       ["data-sets", "data-import", "data-mgmt"],
};

// All sections collapsed by default
const DEFAULT_OPEN = new Set();

export default function Admin() {  // NOSONAR: React component complexity is inherent
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.is_admin ?? false;
  const userTier = user?.subscription_tier ?? "base";
  const TIER_ORDER = { base: 0, rc: 1, sp: 2, elite: 3 };
  const tierAtLeast = (t) => (TIER_ORDER[userTier] ?? 0) >= (TIER_ORDER[t] ?? 0);
  const [activeTab, setActiveTab] = useState(location.state?.tab ?? "settings");
  const [openSections, setOpenSections] = useState(() => {
    const s = new Set(DEFAULT_OPEN);
    if (location.state?.section) s.add(location.state.section);
    return s;
  });
  const [settings, setSettings] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [dictCount, setDictCount] = useState(null);
  const [exportFormat, setExportFormat] = useState("csv");
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreMsg, setRestoreMsg] = useState("");
  const [restoreError, setRestoreError] = useState("");
  const [dmTooltip, setDmTooltip] = useState(null);
  const [dmLoading, setDmLoading] = useState(null);
  const debounceRef = useRef(null);
  const [allSets, setAllSets] = useState([]);
  const [activeBrand, setActiveBrand] = useState(null);
  const [valuesStats, setValuesStats] = useState(null);
  const [seedMsg, setSeedMsg] = useState("");
  const [seedLoading, setSeedLoading] = useState(false);
  const [dedupStats, setDedupStats] = useState(null);
  const [dedupLoading, setDedupLoading] = useState(false);
  const [dedupMsg, setDedupMsg] = useState("");
  const [dedupConfirming, setDedupConfirming] = useState(false);
  const [invalidStats, setInvalidStats] = useState(null);
  const [invalidLoading, setInvalidLoading] = useState(false);
  const [invalidMsg, setInvalidMsg] = useState("");
  const [invalidConfirming, setInvalidConfirming] = useState(false);
  const [bulkImgFile, setBulkImgFile] = useState(null);
  const [bulkImgLoading, setBulkImgLoading] = useState(false);
  const [bulkImgResult, setBulkImgResult] = useState(null);
  const [cvAbbr, setCvAbbr] = useState("");
  const [cvLabel, setCvLabel] = useState("");
  const [wbAbbr, setWbAbbr] = useState("");
  const [wbLabel, setWbLabel] = useState("");
  const [wpAbbr, setWpAbbr] = useState("");
  const [wpLabel, setWpLabel] = useState("");
  const [adminUsers, setAdminUsers] = useState([]);

  const toggleSection = (id) => {
    setOpenSections(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Kiosk: expand/collapse sections and switch tabs programmatically
  useEffect(() => {
    const onExpand  = (e) => setOpenSections(prev => { const next = new Set(prev); next.add(e.detail); return next; });
    const onCollapse = (e) => setOpenSections(prev => { const next = new Set(prev); next.delete(e.detail); return next; });
    const onTab     = (e) => setActiveTab(e.detail);
    window.addEventListener("kiosk-admin-expand",   onExpand);
    window.addEventListener("kiosk-admin-collapse", onCollapse);
    window.addEventListener("kiosk-admin-tab",      onTab);
    return () => {
      window.removeEventListener("kiosk-admin-expand",   onExpand);
      window.removeEventListener("kiosk-admin-collapse", onCollapse);
      window.removeEventListener("kiosk-admin-tab",      onTab);
    };
  }, []);

  const expandCurrentTab = () => {
    const sections = TAB_SECTIONS[activeTab] || [];
    setOpenSections(prev => new Set([...prev, ...sections]));
  };

  const collapseCurrentTab = () => {
    const sections = new Set(TAB_SECTIONS[activeTab] || []);
    setOpenSections(prev => new Set([...prev].filter(s => !sections.has(s))));
  };

  const fetchValuesStats = () => {
    api.get("/dictionary/values-stats")
      .then(res => setValuesStats(res.data))
      .catch(err => console.error("Error fetching values stats:", err));
  };

  useEffect(() => {
    api.get("/settings/")
      .then(res => {
        setSettings({
          ...res.data,
          card_makes: res.data.card_makes || [],
          card_grades: res.data.card_grades || []
        });
      })
      .catch(err => console.error(err));

    api.get("/dictionary/count")
      .then(res => setDictCount(res.data.count))
      .catch(err => console.error("Error fetching dictionary count:", err));

    api.get("/builds/")
      .then(res => setAllSets(res.data))
      .catch(err => console.error("Error fetching sets:", err));

    fetchValuesStats();
  }, []);

  useEffect(() => {
    if (activeTab === "users" && isAdmin) {
      api.get("/admin/users").then(res => setAdminUsers(res.data)).catch(() => {});
    }
  }, [activeTab, isAdmin]);

  const handleUserFeatureToggle = async (userId, field, value) => {
    try {
      await api.put(`/admin/users/${userId}/settings`, { [field]: value });
      setAdminUsers(prev => prev.map(u => u.id === userId ? { ...u, [field]: value } : u));
    } catch (err) { console.error("Failed to update user feature", err); }
  };

  const ALL_NAV_KEYS = ["cards","gallery","balls","builds","sets_binders","wax","packs","analytics"];

  const handleUserTierChange = async (userId, newTier) => {
    try {
      await api.put(`/admin/users/${userId}/tier`, { tier: newTier });
      const res = await api.get("/admin/users");
      setAdminUsers(res.data);
    } catch (err) { console.error("Failed to update tier", err); }
  };
  const handleUserNavToggle = async (userId, key, currentNavItems) => {
    const current = currentNavItems ?? ALL_NAV_KEYS;
    const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
    const newVal = next.length === ALL_NAV_KEYS.length ? null : next;
    try {
      await api.put(`/admin/users/${userId}/settings`, { nav_items: newVal });
      setAdminUsers(prev => prev.map(u => u.id === userId ? { ...u, nav_items: newVal } : u));
    } catch (err) { console.error("Failed to update user nav", err); }
  };

  const handlePublishFactors = async () => {
    if (!window.confirm("Publish current valuation factors to all users? They will be prompted to revalue their collections.")) return;
    try {
      const res = await api.post("/settings/publish-valuation-factors");
      setModalMessage(res.data.message);
      setShowModal(true);
    } catch (err) {
      setModalMessage("Failed to publish factors: " + (err.response?.data?.detail || err.message));
      setShowModal(true);
    }
  };

  const handleSeedFromCards = async () => {
    setSeedLoading(true);
    setSeedMsg("");
    try {
      const res = await api.post("/dictionary/seed-values-from-cards");
      setSeedMsg(res.data.message);
      fetchValuesStats();
    } catch (err) {
      setSeedMsg("Seed failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setSeedLoading(false);
    }
  };

  const handleCheckDuplicates = async () => {
    setDedupLoading(true);
    setDedupMsg("");
    setDedupStats(null);
    setDedupConfirming(false);
    try {
      const res = await api.get("/dictionary/duplicate-stats");
      setDedupStats(res.data);
    } catch (err) {
      setDedupMsg("Check failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setDedupLoading(false);
    }
  };

  const handleDeduplicate = async () => {
    setDedupLoading(true);
    setDedupMsg("");
    try {
      const res = await api.post("/dictionary/deduplicate");
      setDedupMsg(res.data.message);
      setDedupStats(null);
      setDedupConfirming(false);
      api.get("/dictionary/count").then(r => setDictCount(r.data.count)).catch(() => {});
    } catch (err) {
      setDedupMsg("Dedup failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setDedupLoading(false);
    }
  };

  const handleCheckInvalid = async () => {
    setInvalidLoading(true);
    setInvalidMsg("");
    setInvalidStats(null);
    setInvalidConfirming(false);
    try {
      const res = await api.get("/dictionary/invalid-stats");
      setInvalidStats(res.data);
    } catch (err) {
      setInvalidMsg("Check failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setInvalidLoading(false);
    }
  };

  const handlePurgeInvalid = async () => {
    setInvalidLoading(true);
    setInvalidMsg("");
    try {
      const res = await api.post("/dictionary/purge-invalid");
      setInvalidMsg(res.data.message);
      setInvalidStats(null);
      setInvalidConfirming(false);
      api.get("/dictionary/count").then(r => setDictCount(r.data.count)).catch(() => {});
    } catch (err) {
      setInvalidMsg("Purge failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setInvalidLoading(false);
    }
  };

  const handleBulkImageImport = async () => {
    if (!bulkImgFile) return;
    setBulkImgLoading(true);
    setBulkImgResult(null);
    try {
      const formData = new FormData();
      formData.append("file", bulkImgFile);
      const res = await api.post("/admin/bulk-image-import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setBulkImgResult(res.data);
      setBulkImgFile(null);
    } catch (err) {
      setBulkImgResult({ error: err.response?.data?.detail || "Import failed." });
    } finally {
      setBulkImgLoading(false);
    }
  };

  const debouncedSave = (updatedSettings) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await api.put("/settings/", updatedSettings);
        setSettings(res.data);
        window.dispatchEvent(new Event("settings-changed"));
      } catch (err) {
        console.error(err);
      }
    }, 1500);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...settings, [name]: value };
    setSettings(updated);
    debouncedSave(updated);
  };

  const handleToggle = async (field) => {
    try {
      const updated = { ...settings, [field]: !settings[field] };
      const res = await api.put("/settings/", updated);
      setSettings(res.data);
      if (field === "dark_mode") {
        document.documentElement.setAttribute("data-theme", res.data.dark_mode ? "dark" : "light");
      }
      window.dispatchEvent(new Event("settings-changed"));
    } catch (err) {
      console.error("Error updating settings:", err);
    }
  };

  const handleExport = async () => {
    setDmLoading("export");
    try {
      const ext = exportFormat;
      const res = await api.get("/cards/export", {
        params: { format: exportFormat },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `cards.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setDmLoading(null);
    }
  };

  const handleBackup = async () => {
    setDmLoading("backup");
    try {
      const res = await api.get("/cards/backup", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "cardstoard_backup.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Backup failed:", err);
    } finally {
      setDmLoading(null);
    }
  };

  const handleRestore = async () => {
    if (!restoreFile) return;
    if (!window.confirm("This will REPLACE all your current cards with the backup. Continue?")) return;
    setDmLoading("restore");
    try {
      const formData = new FormData();
      formData.append("file", restoreFile);
      const res = await api.post("/cards/restore", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRestoreMsg(res.data.message || `Restored ${res.data.restored} cards.`);
      setRestoreError("");
      setRestoreFile(null);
    } catch (err) {
      setRestoreError(err.response?.data?.detail || "Restore failed.");
      setRestoreMsg("");
    } finally {
      setDmLoading(null);
    }
  };

  const InfoIcon = ({ id, text }) => ( // NOSONAR
    <span style={{ position: "relative", display: "inline-block", marginLeft: "0.4rem" }} onClick={e => e.stopPropagation()}>
      <span
        onMouseEnter={() => setDmTooltip(id)}
        onMouseLeave={() => setDmTooltip(null)}
        style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: "16px", height: "16px", borderRadius: "50%",
          background: "#aaa", color: "#fff", fontSize: "0.7rem",
          fontWeight: 700, cursor: "default", userSelect: "none",
        }}
      >i</span>
      {dmTooltip === id && (
        <span style={{
          position: "absolute", bottom: "calc(100% + 6px)", left: "50%",
          transform: "translateX(-50%)",
          background: "#333", color: "#fff", fontSize: "0.78rem",
          padding: "0.4rem 0.65rem", borderRadius: "6px",
          whiteSpace: "nowrap", zIndex: 100,
          boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
        }}>
          {text}
        </span>
      )}
    </span>
  );

  // Accordion section header — clickable h3 with animated chevron
  const SectionHeader = ({ id, children }) => (
    <h3
      onClick={() => toggleSection(id)}
      style={{
        cursor: "pointer",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 0,
        userSelect: "none",
      }}
    >
      <span>{children}</span>
      <span style={{
        fontSize: "0.7rem",
        color: "var(--text-muted)",
        display: "inline-block",
        transform: openSections.has(id) ? "rotate(180deg)" : "rotate(0deg)",
        transition: "transform 0.2s",
        marginLeft: "0.5rem",
      }}>▼</span>
    </h3>
  );

  // --- Set visibility helpers ---
  const setsByBrand = allSets.reduce((acc, s) => {
    (acc[s.brand] = acc[s.brand] || []).push(s);
    return acc;
  }, {});
  const brands = Object.keys(setsByBrand).sort((a, b) => a.localeCompare(b));

  const isSetVisible = (id) =>
    !settings || settings.visible_set_ids === null || (settings.visible_set_ids || []).includes(id);

  const visibleCountForBrand = (brand) => {
    if (!settings || settings.visible_set_ids === null) return setsByBrand[brand]?.length ?? 0;
    return (setsByBrand[brand] ?? []).filter(s => (settings.visible_set_ids || []).includes(s.id)).length;
  };

  const toggleYear = (setId) => {
    const allIds = allSets.map(s => s.id);
    const currentIds = settings.visible_set_ids ?? allIds;
    const next = currentIds.includes(setId)
      ? currentIds.filter(x => x !== setId)
      : [...currentIds, setId];
    const value = next.length === allIds.length ? null : next;
    setSettings(prev => ({ ...prev, visible_set_ids: value }));
    debouncedSave({ visible_set_ids: value });
  };

  const selectAllForBrand = (brand) => {
    const brandIds = setsByBrand[brand].map(s => s.id);
    const allIds = allSets.map(s => s.id);
    const currentIds = settings.visible_set_ids ?? allIds;
    const next = [...new Set([...currentIds, ...brandIds])];
    const value = next.length === allIds.length ? null : next;
    setSettings(prev => ({ ...prev, visible_set_ids: value }));
    debouncedSave({ visible_set_ids: value });
  };

  const clearAllForBrand = (brand) => {
    const brandIds = new Set(setsByBrand[brand].map(s => s.id));
    const allIds = allSets.map(s => s.id);
    const currentIds = settings.visible_set_ids ?? allIds;
    const next = currentIds.filter(id => !brandIds.has(id));
    setSettings(prev => ({ ...prev, visible_set_ids: next }));
    debouncedSave({ visible_set_ids: next });
  };

  if (!settings) return (
    <>
      <AppHeader />
      <div className="container" style={{ textAlign: "center", marginTop: "3rem" }}>
        <div className="cs-spinner" />
        <p style={{ color: "var(--text-muted)", marginTop: "1rem", fontSize: "0.95rem" }}>Loading settings…</p>
      </div>
    </>
  );

  return (
    <>
      <AppHeader />
      <div className="container" style={activeTab === "users" ? { maxWidth: "95vw" } : {}}>
        <h2 className="page-header">Admin Settings</h2>

        {/* ── Tab bar ── */}
        <div className="admin-tab-bar">
          {TABS.filter(tab => tab.id !== "users" || isAdmin).map(tab => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`admin-tab-btn${activeTab === tab.id ? " active" : ""}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Expand / Collapse All (only for tabs with accordion sections) */}
        {TAB_SECTIONS[activeTab] && (
          <div style={{ textAlign: "right", marginBottom: "0.5rem" }}>
            <button type="button" onClick={expandCurrentTab}
              style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: "0.8rem", cursor: "pointer", padding: "0 0.4rem" }}>
              Expand All
            </button>
            <span style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>|</span>
            <button type="button" onClick={collapseCurrentTab}
              style={{ background: "none", border: "none", color: "var(--text-muted)", fontSize: "0.8rem", cursor: "pointer", padding: "0 0.4rem" }}>
              Collapse All
            </button>
          </div>
        )}

        {/* ══════════════════════════════════════════
            TAB: Settings  (4 accordion sections)
        ══════════════════════════════════════════ */}
        {activeTab === "settings" && (
          <>
            {/* Features */}
            <div className="card-section" style={{ marginBottom: "1rem" }}>
              <SectionHeader id="s-features">Features <InfoIcon id="features" text="Toggle optional app features on or off. Changes take effect immediately." /></SectionHeader>
              {openSections.has("s-features") && (
                <div data-tour="admin-features" style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
                  {tierAtLeast("rc") && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input type="checkbox" checked={settings.enable_smart_fill} onChange={() => handleToggle("enable_smart_fill")}
                        style={{ width: "16px", height: "16px", margin: 0, flexShrink: 0, cursor: "pointer" }} />
                      <div style={{ display: "flex", alignItems: "center", fontSize: "0.9rem", fontWeight: "bold" }}>
                        Smart Fill
                        <InfoIcon id="smartfill" text="Auto-populates card number and rookie flag when adding cards, using the Player Dictionary." />
                      </div>
                    </div>
                  )}
                  {tierAtLeast("elite") && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input type="checkbox" checked={settings.chatbot_enabled ?? false} onChange={() => handleToggle("chatbot_enabled")}
                        style={{ width: "16px", height: "16px", margin: 0, flexShrink: 0, cursor: "pointer" }} />
                      <div style={{ display: "flex", alignItems: "center", fontSize: "0.9rem", fontWeight: "bold" }}>
                        Collection Assistant (Chatbot)
                        <InfoIcon id="chatbot" text="Enables the AI-powered chat assistant (💬) in the header. Requires an Anthropic API key to be configured." />
                      </div>
                    </div>
                  )}
                  {tierAtLeast("elite") && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input type="checkbox" checked={settings.enable_image_ai ?? false} onChange={() => handleToggle("enable_image_ai")}
                        style={{ width: "16px", height: "16px", margin: 0, flexShrink: 0, cursor: "pointer" }} />
                      <div style={{ display: "flex", alignItems: "center", fontSize: "0.9rem", fontWeight: "bold" }}>
                        Image AI (Scan)
                        <InfoIcon id="imageai" text="Enable AI-powered card identification from photos and QR code scanning. Uses Claude Vision API — requires ANTHROPIC_API_KEY." />
                      </div>
                    </div>
                  )}
                  {tierAtLeast("sp") && (
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input type="checkbox" checked={settings.enable_graded_cards ?? false} onChange={() => handleToggle("enable_graded_cards")}
                        style={{ width: "16px", height: "16px", margin: 0, flexShrink: 0, cursor: "pointer" }} />
                      <div style={{ display: "flex", alignItems: "center", fontSize: "0.9rem", fontWeight: "bold" }}>
                        Graded Cards
                        <InfoIcon id="gradedcards" text="Enables PSA/SGC/BGS graded card tracking. Graded cards use a 1–10 numeric grade and manually-entered market value instead of the book-value formula." />
                      </div>
                    </div>
                  )}
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "0.5rem" }}>
                    <input type="checkbox" checked={settings.dark_mode ?? false} onChange={() => handleToggle("dark_mode")}
                      style={{ width: "16px", height: "16px", margin: 0, flexShrink: 0, cursor: "pointer" }} />
                    <div style={{ display: "flex", alignItems: "center", fontSize: "0.9rem", fontWeight: "bold" }}>
                      Dark Mode
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Nav Bar */}
            {(() => {
              const ALL_NAV = [
                { key: "cards",        label: "📇 Cards",       tier: "base" },
                { key: "gallery",      label: "🎴 Gallery",     tier: "rc"   },
                { key: "balls",        label: "⚾ Balls",        tier: "sp"   },
                { key: "builds",       label: "🏗️ Builds",      tier: "sp"   },
                { key: "sets_binders", label: "📓 Sets/Binders", tier: "sp"  },
                { key: "wax",          label: "📦 Wax",          tier: "sp"   },
                { key: "packs",        label: "🧧 Packs",        tier: "sp"   },
                { key: "analytics",    label: "📊 Analytics",    tier: "rc"   },
              ];
              const current = settings.nav_items ?? ALL_NAV.map(n => n.key);
              const toggle = (key) => {
                const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
                const value = next.length === ALL_NAV.map(n => n.key).length ? null : next;
                setSettings(prev => ({ ...prev, nav_items: value }));
                debouncedSave({ nav_items: value });
              };
              return (
                <div className="card-section" style={{ marginBottom: "1rem" }}>
                  <SectionHeader id="s-navbar">
                    Nav Bar <InfoIcon id="navbar" text="Choose which buttons appear in the navigation bar. Items greyed out require a higher tier." />
                  </SectionHeader>
                  {openSections.has("s-navbar") && (
                    <div style={{ marginTop: "0.75rem", display: "flex", flexDirection: "column", gap: "0.6rem", alignItems: "center" }}>
                      {[ALL_NAV.slice(0, 4), ALL_NAV.slice(4)].map((row, ri) => (
                        <div key={ri} style={{ display: "flex", gap: "1.5rem" }}>  {/* NOSONAR */}
                          {row.map(({ key, label, tier }) => {
                            const allowed = tierAtLeast(tier);
                            return (
                              <label key={key} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.9rem", fontWeight: "bold",
                                cursor: allowed ? "pointer" : "not-allowed", opacity: allowed ? 1 : 0.38 }}
                                title={!allowed ? `Requires ${tier.toUpperCase()} plan` : undefined}>
                                <input type="checkbox" checked={allowed && current.includes(key)}
                                  disabled={!allowed}
                                  onChange={() => allowed && toggle(key)}
                                  style={{ width: "16px", height: "16px", margin: 0, cursor: allowed ? "pointer" : "not-allowed" }} />
                                {label}
                              </label>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Card Variants */}
            {(() => {
              const ALL_VARIANTS = [
                { key: "refractor",    abbr: "Ref",  label: "Refractor" },
                { key: "short_print",  abbr: "SP",   label: "Short Print" },
                { key: "autograph",    abbr: "Auto", label: "Autograph" },
                { key: "traded",       abbr: "Trd",  label: "Traded" },
                { key: "artists_proof",abbr: "AP",   label: "Artist's Proof" },
                { key: "press_proof",  abbr: "PP",   label: "Press Proof" },
                { key: "gold",         abbr: "Gld",  label: "Gold" },
                { key: "tiffany",      abbr: "Tiff", label: "Tiffany" },
                { key: "update",       abbr: "Upd",  label: "Update" },
                { key: "error",        abbr: "Err",  label: "Error" },
                { key: "nno",          abbr: "NNO",  label: "No Number On" },
              ];
              const current = settings.card_variants ?? ALL_VARIANTS.map(v => v.key);
              const toggle = (key) => {
                const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
                const value = next.length === ALL_VARIANTS.length ? null : next;
                setSettings(prev => ({ ...prev, card_variants: value }));
                debouncedSave({ card_variants: value });
              };

              const custom = settings.custom_variants ?? [];
              const MAX_CUSTOM = 20;
              const addCustom = () => {
                const abbr = cvAbbr.trim().toUpperCase().slice(0, 4);
                const label = cvLabel.trim();
                if (!abbr || !label) return;
                if (custom.length >= MAX_CUSTOM) return;
                if (custom.some(c => c.abbr.toUpperCase() === abbr)) return;
                const key = "cv_" + abbr.toLowerCase();
                const next = [...custom, { key, abbr, label }];
                setSettings(prev => ({ ...prev, custom_variants: next }));
                debouncedSave({ custom_variants: next });
                setCvAbbr(""); setCvLabel("");
              };
              const removeCustom = (key) => {
                const next = custom.filter(c => c.key !== key);
                setSettings(prev => ({ ...prev, custom_variants: next.length ? next : null }));
                debouncedSave({ custom_variants: next.length ? next : null });
              };

              const inpSm = { padding: "0.3rem 0.5rem", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-primary)", fontSize: "0.85rem" };

              return (
                <div className="card-section" style={{ marginBottom: "1rem" }}>
                  <SectionHeader id="s-variants">
                    Card Variants <InfoIcon id="variants" text="Choose which built-in variant types appear in the card editing panel, and add your own custom types (max 4 chars, max 20 custom)." />
                  </SectionHeader>
                  {openSections.has("s-variants") && (
                    <div style={{ marginTop: "0.75rem" }}>
                      {/* Built-in toggles */}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, auto)", gap: "0.4rem 1.5rem", justifyContent: "start", marginBottom: "1rem" }}>
                        {ALL_VARIANTS.map(({ key, abbr, label }) => (
                          <label key={key} title={label} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "0.9rem", cursor: "pointer", whiteSpace: "nowrap" }}>
                            <input type="checkbox" checked={current.includes(key)} onChange={() => toggle(key)}
                              style={{ width: "16px", height: "16px", margin: 0, cursor: "pointer" }} />
                            {abbr}
                          </label>
                        ))}
                      </div>

                      {/* Custom variants */}
                      <div style={{ borderTop: "1px solid var(--border)", paddingTop: "0.75rem" }}>
                        <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "0.5rem" }}>
                          Custom Variants ({custom.length}/{MAX_CUSTOM})
                        </div>
                        {custom.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.6rem" }}>
                            {custom.map(({ key, abbr, label }) => (
                              <span key={key} title={label} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--bg-muted)", border: "1px solid var(--border)", borderRadius: 6, padding: "2px 8px", fontSize: "0.82rem" }}>
                                <strong>{abbr}</strong>
                                <span style={{ color: "var(--text-muted)", fontSize: "0.78rem" }}>{label}</span>
                                <button onClick={() => removeCustom(key)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: "0.9rem", padding: 0, marginLeft: 2 }} title="Remove">✕</button>
                              </span>
                            ))}
                          </div>
                        )}
                        {custom.length < MAX_CUSTOM && (
                          <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                            <input value={cvAbbr} onChange={e => setCvAbbr(e.target.value.slice(0, 4))} placeholder="Abbr (4 chars)" maxLength={4}
                              style={{ ...inpSm, width: 100 }}
                              onKeyDown={e => { if (e.key === "Enter") addCustom(); }} />
                            <input value={cvLabel} onChange={e => setCvLabel(e.target.value)} placeholder="Full name / description"
                              style={{ ...inpSm, flex: 1, minWidth: 160 }}
                              onKeyDown={e => { if (e.key === "Enter") addCustom(); }} />
                            <button className="nav-btn secondary" onClick={addCustom} style={{ fontSize: "0.85rem", padding: "0.3rem 0.8rem" }}>+ Add</button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Wax Box Types */}
            {(() => {
              const ALL_BOX_TYPES = [
                { key: "cello",   label: "Cello",   color: "#1d4ed8" },
                { key: "rack",    label: "Rack",    color: "#d97706" },
                { key: "std",     label: "Std",     color: "#16a34a" },
                { key: "vending", label: "Vending", color: "#0891b2" },
              ];
              const currentBox = settings.wax_box_types ?? ALL_BOX_TYPES.map(t => t.key);
              const toggleBox = (key) => {
                const next = currentBox.includes(key) ? currentBox.filter(k => k !== key) : [...currentBox, key];
                const value = next.length === ALL_BOX_TYPES.length ? null : next;
                setSettings(prev => ({ ...prev, wax_box_types: value }));
                debouncedSave({ wax_box_types: value });
              };
              const customBox = settings.custom_wax_box_types ?? [];
              const addCustomBox = () => {
                const abbr = wbAbbr.trim().toUpperCase().slice(0, 8);
                const label = wbLabel.trim();
                if (!abbr || !label) return;
                if (customBox.some(c => c.key === abbr.toLowerCase())) return;
                const next = [...customBox, { key: abbr.toLowerCase(), label }];
                setSettings(prev => ({ ...prev, custom_wax_box_types: next }));
                debouncedSave({ custom_wax_box_types: next });
                setWbAbbr(""); setWbLabel("");
              };
              const removeCustomBox = (key) => {
                const next = customBox.filter(c => c.key !== key);
                setSettings(prev => ({ ...prev, custom_wax_box_types: next.length ? next : null }));
                debouncedSave({ custom_wax_box_types: next.length ? next : null });
              };
              const inpSm = { padding: "0.3rem 0.5rem", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-primary)", fontSize: "0.85rem" };
              return (
                <div className="card-section" style={{ marginBottom: "1rem" }}>
                  <SectionHeader id="s-wax-box-types">
                    Wax Box Types <InfoIcon id="waxtypes" text="Toggle built-in wax box types and add custom ones. Active types appear in the Type selector when adding or editing a wax box." />
                  </SectionHeader>
                  {openSections.has("s-wax-box-types") && (
                    <div style={{ marginTop: "0.75rem" }}>
                      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                        {ALL_BOX_TYPES.map(({ key, label, color }) => (
                          <label key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.9rem", cursor: "pointer" }}>
                            <input type="checkbox" checked={currentBox.includes(key)} onChange={() => toggleBox(key)}
                              style={{ width: 16, height: 16, margin: 0, cursor: "pointer" }} />
                            <span style={{ background: color, color: "#fff", borderRadius: 4, padding: "1px 8px", fontSize: "0.8rem", fontWeight: 600 }}>{label}</span>
                          </label>
                        ))}
                      </div>
                      <div style={{ borderTop: "1px solid var(--border)", paddingTop: "0.75rem" }}>
                        <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "0.5rem" }}>Custom Types</div>
                        {customBox.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.6rem" }}>
                            {customBox.map(({ key, label }) => (
                              <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--bg-muted)", border: "1px solid var(--border)", borderRadius: 6, padding: "2px 8px", fontSize: "0.82rem" }}>
                                <strong>{label}</strong>
                                <button onClick={() => removeCustomBox(key)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: "0.9rem", padding: 0, marginLeft: 2 }}>✕</button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                          <input value={wbAbbr} onChange={e => setWbAbbr(e.target.value.slice(0, 8))} placeholder="Key (8 chars)" maxLength={8}
                            style={{ ...inpSm, width: 110 }} onKeyDown={e => { if (e.key === "Enter") addCustomBox(); }} />
                          <input value={wbLabel} onChange={e => setWbLabel(e.target.value)} placeholder="Display name"
                            style={{ ...inpSm, flex: 1, minWidth: 160 }} onKeyDown={e => { if (e.key === "Enter") addCustomBox(); }} />
                          <button className="nav-btn secondary" onClick={addCustomBox} style={{ fontSize: "0.85rem", padding: "0.3rem 0.8rem" }}>+ Add</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* Wax Pack Types */}
            {(() => {
              const ALL_PACK_TYPES = [
                { key: "cello",   label: "Cello",   color: "#1d4ed8" },
                { key: "rack",    label: "Rack",    color: "#d97706" },
                { key: "wax",     label: "Wax",     color: "#16a34a" },
                { key: "blister", label: "Blister", color: "#7c3aed" },
                { key: "vending", label: "Vending", color: "#0891b2" },
              ];
              const currentPack = settings.wax_pack_types ?? ALL_PACK_TYPES.map(t => t.key);
              const togglePack = (key) => {
                const next = currentPack.includes(key) ? currentPack.filter(k => k !== key) : [...currentPack, key];
                const value = next.length === ALL_PACK_TYPES.length ? null : next;
                setSettings(prev => ({ ...prev, wax_pack_types: value }));
                debouncedSave({ wax_pack_types: value });
              };
              const customPack = settings.custom_wax_pack_types ?? [];
              const addCustomPack = () => {
                const abbr = wpAbbr.trim().toUpperCase().slice(0, 8);
                const label = wpLabel.trim();
                if (!abbr || !label) return;
                if (customPack.some(c => c.key === abbr.toLowerCase())) return;
                const next = [...customPack, { key: abbr.toLowerCase(), label }];
                setSettings(prev => ({ ...prev, custom_wax_pack_types: next }));
                debouncedSave({ custom_wax_pack_types: next });
                setWpAbbr(""); setWpLabel("");
              };
              const removeCustomPack = (key) => {
                const next = customPack.filter(c => c.key !== key);
                setSettings(prev => ({ ...prev, custom_wax_pack_types: next.length ? next : null }));
                debouncedSave({ custom_wax_pack_types: next.length ? next : null });
              };
              const inpSm = { padding: "0.3rem 0.5rem", borderRadius: 4, border: "1px solid var(--border)", background: "var(--bg-input)", color: "var(--text-primary)", fontSize: "0.85rem" };
              return (
                <div className="card-section" style={{ marginBottom: "1rem" }}>
                  <SectionHeader id="s-wax-pack-types">
                    Wax Pack Types <InfoIcon id="packtypes" text="Toggle built-in wax pack types and add custom ones. Active types appear in the Type selector when adding or editing a wax pack." />
                  </SectionHeader>
                  {openSections.has("s-wax-pack-types") && (
                    <div style={{ marginTop: "0.75rem" }}>
                      <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", marginBottom: "1rem" }}>
                        {ALL_PACK_TYPES.map(({ key, label, color }) => (
                          <label key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "0.9rem", cursor: "pointer" }}>
                            <input type="checkbox" checked={currentPack.includes(key)} onChange={() => togglePack(key)}
                              style={{ width: 16, height: 16, margin: 0, cursor: "pointer" }} />
                            <span style={{ background: color, color: "#fff", borderRadius: 4, padding: "1px 8px", fontSize: "0.8rem", fontWeight: 600 }}>{label}</span>
                          </label>
                        ))}
                      </div>
                      <div style={{ borderTop: "1px solid var(--border)", paddingTop: "0.75rem" }}>
                        <div style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--text-secondary)", marginBottom: "0.5rem" }}>Custom Types</div>
                        {customPack.length > 0 && (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.6rem" }}>
                            {customPack.map(({ key, label }) => (
                              <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--bg-muted)", border: "1px solid var(--border)", borderRadius: 6, padding: "2px 8px", fontSize: "0.82rem" }}>
                                <strong>{label}</strong>
                                <button onClick={() => removeCustomPack(key)} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: "0.9rem", padding: 0, marginLeft: 2 }}>✕</button>
                              </span>
                            ))}
                          </div>
                        )}
                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                          <input value={wpAbbr} onChange={e => setWpAbbr(e.target.value.slice(0, 8))} placeholder="Key (8 chars)" maxLength={8}
                            style={{ ...inpSm, width: 110 }} onKeyDown={e => { if (e.key === "Enter") addCustomPack(); }} />
                          <input value={wpLabel} onChange={e => setWpLabel(e.target.value)} placeholder="Display name"
                            style={{ ...inpSm, flex: 1, minWidth: 160 }} onKeyDown={e => { if (e.key === "Enter") addCustomPack(); }} />
                          <button className="nav-btn secondary" onClick={addCustomPack} style={{ fontSize: "0.85rem", padding: "0.3rem 0.8rem" }}>+ Add</button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {/* General Settings */}
            <div className="card-section" style={{ marginBottom: "1rem" }}>
              <SectionHeader id="s-general">
                General Settings <InfoIcon id="generalsettings" text="Configure the list of card brands and grades available when adding cards." />
              </SectionHeader>
              {openSections.has("s-general") && (
                <div style={{ marginTop: "0.75rem" }}>
                  {/* Cards Per Page */}
                  <div data-tour="admin-cards-per-page" style={{ marginBottom: "1rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "0.75rem" }}>
                    <label style={{ fontWeight: 600, color: "var(--text-secondary)", fontSize: "0.9rem" }}>
                      Cards Per Page
                      <InfoIcon id="cardsperpage" text="How many cards to load at a time in My Cards. 'All' loads your entire collection at once." />
                    </label>
                    <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
                      {[100, 250, 500, 1000].map(n => (
                        <button key={n}
                          onClick={() => { setSettings(prev => ({ ...prev, cards_per_page: n })); debouncedSave({ cards_per_page: n }); }}
                          style={{ padding: "3px 10px", borderRadius: 6, border: "1px solid var(--border)", fontSize: "0.85rem", cursor: "pointer",
                            background: settings.cards_per_page === n ? "#1976d2" : "var(--bg-muted)",
                            color: settings.cards_per_page === n ? "#fff" : "var(--text-primary)" }}>
                          {n}
                        </button>
                      ))}
                      <button
                        onClick={() => { setSettings(prev => ({ ...prev, cards_per_page: null })); debouncedSave({ cards_per_page: null }); }}
                        style={{ padding: "3px 10px", borderRadius: 6, border: "1px solid var(--border)", fontSize: "0.85rem", cursor: "pointer",
                          background: settings.cards_per_page === null || settings.cards_per_page === undefined ? "#1976d2" : "var(--bg-muted)",
                          color: settings.cards_per_page === null || settings.cards_per_page === undefined ? "#fff" : "var(--text-primary)" }}>
                        All
                      </button>
                    </div>
                  </div>
                  <ChipsInput
                    label="Card Makes"
                    values={settings.card_makes}
                    setValues={(vals) => {
                      const updated = { ...settings, card_makes: vals };
                      setSettings(updated);
                      debouncedSave(updated);
                    }}
                  />
                  <div style={{ marginTop: "1rem" }}>
                    <label style={{ fontWeight: 750, color: "var(--text-secondary)", display: "block", marginBottom: "0.4rem", textAlign: "center" }}>
                      Card Grades
                      <InfoIcon id="cardgrades" text="Fixed by the CardStoard valuation formula — not user-configurable." />
                    </label>
                    <div style={{ display: "flex", flexWrap: "nowrap", gap: "0.4rem", justifyContent: "center" }}>
                      {[
                        { label: "3.0 NM-MT+", bg: "#9333ea" },
                        { label: "1.5 NM",     bg: "#2563eb" },
                        { label: "1.0 EX",     bg: "#5bc0de" },
                        { label: "0.8 VG",     bg: "#f97316" },
                        { label: "0.4 FR",     bg: "#dc2626" },
                        { label: "0.2 PR",     bg: "#777"    },
                      ].map(({ label, bg }) => (
                        <span key={label} style={{
                          background: bg, borderRadius: "20px", padding: "0.25rem 0.75rem",
                          fontSize: "0.85rem", color: "#fff",
                        }}>{label}</span>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Row Colors */}
            <div className="card-section" style={{ marginBottom: "1rem" }}>
              <SectionHeader id="s-rowcolors">
                Row Colors <InfoIcon id="rowcolors" text="Background colors applied to rows on the My Cards page based on card condition." />
              </SectionHeader>
              {openSections.has("s-rowcolors") && (
                <div data-tour="admin-row-colors" style={{ marginTop: "0.75rem" }}>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "2rem", flexWrap: "nowrap", margin: "0.5rem 0" }}>
                    {[
                      { label: "Rookie", name: "row_color_rookie" },
                      { label: "Grade 3 (NM-MT+)", name: "row_color_grade3" },
                      { label: "Rookie + Grade 3", name: "row_color_rookie_grade3" },
                    ].map(({ label, name }) => (
                      <div key={name} style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <label style={{ fontSize: "0.95rem", whiteSpace: "nowrap" }}>{label}</label>
                        <input type="color" name={name} value={settings[name] || "#ffffff"} onChange={handleChange}
                          style={{ width: "52px", height: "36px", padding: "2px", cursor: "pointer", border: "1px solid #ccc", borderRadius: "4px" }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ textAlign: "center", marginTop: "0.5rem" }}>
                    <button type="button" className="nav-btn" onClick={() => {
                      const defaults = { ...settings, row_color_rookie: "#fff3c4", row_color_grade3: "#e8dcff", row_color_rookie_grade3: "#b8d8f7" };
                      setSettings(defaults);
                      debouncedSave(defaults);
                    }}>
                      Restore Default Colors
                    </button>
                  </div>
                </div>
              )}
            </div>

          </>
        )}

        {/* ══════════════════════════════════════════
            TAB: Valuation  (flat — short enough)
        ══════════════════════════════════════════ */}
        {activeTab === "valuation" && (
          <>
            {/* Pending revaluation banner — non-admin only */}
            {!isAdmin && settings?.valuation_pending && (
              <div style={{ background: "var(--warn-bg)", border: "1px solid var(--warn-border)", borderRadius: 8,
                padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: "0.9rem", textAlign: "center",
                color: "var(--warn-text)" }}>
                ⚡ CardStoard has updated market factors. Click <strong>Apply Global Valuation</strong> below to apply the latest pricing to your collection.
              </div>
            )}

            {/* Factor inputs — admin only */}
            {isAdmin && (
              <div className="card-section">
                <h3>Factor Settings <InfoIcon id="factorsettings" text="Proprietary CardStoard valuation multipliers. Edit here then click Publish to push to all users." /></h3>
                <div className="factor-group">
                  <div><label htmlFor="rookie_factor">Rookie Factor</label><input id="rookie_factor" type="number" step="0.01" name="rookie_factor" value={settings.rookie_factor} onChange={handleChange} /></div>
                  <div><label htmlFor="auto_factor">Auto Factor</label><input id="auto_factor" type="number" step="0.01" name="auto_factor" value={settings.auto_factor} onChange={handleChange} /></div>
                  <div><label htmlFor="rookie_mt_factor">Rookie NM-MT+ Factor</label><input id="rookie_mt_factor" type="number" step="0.01" name="rookie_mt_factor" value={settings.rookie_mt_factor ?? 1.00} onChange={handleChange} /></div>
                  <div><label htmlFor="mtgrade_factor">NM-MT+ Factor</label><input id="mtgrade_factor" type="number" step="0.01" name="mtgrade_factor" value={settings.mtgrade_factor} onChange={handleChange} /></div>
                  <div><label htmlFor="exgrade_factor">NM Factor</label><input id="exgrade_factor" type="number" step="0.01" name="exgrade_factor" value={settings.exgrade_factor} onChange={handleChange} /></div>
                  <div><label htmlFor="vggrade_factor">EX Factor</label><input id="vggrade_factor" type="number" step="0.01" name="vggrade_factor" value={settings.vggrade_factor} onChange={handleChange} /></div>
                  <div><label htmlFor="gdgrade_factor">VG Factor</label><input id="gdgrade_factor" type="number" step="0.01" name="gdgrade_factor" value={settings.gdgrade_factor} onChange={handleChange} /></div>
                  <div><label htmlFor="frgrade_factor">FR Factor</label><input id="frgrade_factor" type="number" step="0.01" name="frgrade_factor" value={settings.frgrade_factor} onChange={handleChange} /></div>
                  <div><label htmlFor="prgrade_factor">PR Factor</label><input id="prgrade_factor" type="number" step="0.01" name="prgrade_factor" value={settings.prgrade_factor} onChange={handleChange} /></div>
                </div>
                <div style={{ textAlign: "center", marginTop: "1rem" }}>
                  <button className="nav-btn" onClick={handlePublishFactors}>
                    📢 Publish Factors to All Users
                  </button>
                </div>
              </div>
            )}

            <div data-tour="admin-valuation-apply" style={{ marginTop: "1.5rem", display: "flex", justifyContent: "center", gap: "0.75rem", flexWrap: "wrap" }}>
              <div style={{ textAlign: "center" }}>
                <button onClick={async () => {
                  if (!window.confirm("Recalculate valuation for ALL cards now?")) return;
                  try {
                    const res = await api.post("/cards/revalue-all");
                    const sr = await api.get("/settings/");
                    setSettings(sr.data);
                    setModalMessage(res.data.message || `💰 Revalued ${res.data.updated} cards.`);
                    setShowModal(true);
                  } catch (err) {
                    console.error(err);
                    setModalMessage("❌ Error applying valuation. See console for details.");
                    setShowModal(true);
                  }
                }} className="val-btn">
                  💰 Apply Global Valuation 💰
                </button>
                <InfoIcon id="revalue" text="Recalculates the estimated value for every card in your collection using current factor settings." />
                {settings?.last_valuation_at && (
                  <div style={{ fontSize: "0.78rem", color: "var(--text-muted)", marginTop: "0.3rem" }}>
                    Last applied: {new Date(settings.last_valuation_at).toLocaleString()}
                  </div>
                )}
              </div>
              <div style={{ textAlign: "center" }}>
                <button type="button" onClick={async () => {
                  if (!window.confirm("Reset the book freshness timer to today for all cards that have book values entered?")) return;
                  try {
                    const res = await api.post("/cards/refresh-all-book-values");
                    setModalMessage(res.data.message || `↻ Reset freshness for ${res.data.updated} cards.`);
                    setShowModal(true);
                  } catch (err) {
                    console.error(err);
                    setModalMessage("❌ Error resetting freshness timers. See console for details.");
                    setShowModal(true);
                  }
                }} className="val-btn">
                  ⏱️ Reset Book Value Timers ⏱️
                </button>
                <InfoIcon id="refreshbook" text="Marks today as the book-value update date for every card that has book values entered. Use this to establish a baseline after a bulk review." />
              </div>
              <div style={{ textAlign: "center" }}>
                <button type="button" onClick={async () => {
                  if (!window.confirm("Clear the book freshness timer for ALL cards? Every card will be flagged as needing a book value review.")) return;
                  try {
                    const res = await api.post("/cards/clear-book-freshness");
                    setModalMessage(res.data.message || `Cleared freshness timer for ${res.data.updated} cards.`);
                    setShowModal(true);
                  } catch (err) {
                    console.error(err);
                    setModalMessage("❌ Error clearing freshness timers. See console for details.");
                    setShowModal(true);
                  }
                }} className="val-btn">
                  🔴 Clear Book Value Timers 🔴
                </button>
                <InfoIcon id="clearbook" text="Clears the book-value update date for every card, flagging all cards as needing a price review. Use this before a bulk repricing session." />
              </div>
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════
            TAB: Dictionary  (2 accordion sections)
        ══════════════════════════════════════════ */}
        {activeTab === "dictionary" && (
          <>
            {/* Player Dictionary */}
            <div className="card-section" style={{ marginBottom: "1rem" }}>
              <SectionHeader id="d-player">
                Player Dictionary <InfoIcon id="dictionary" text="A searchable database of players, brands, years, and card numbers used by Smart Fill and collection highlights." />
              </SectionHeader>
              {openSections.has("d-player") && (
                <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
                  <p>Total entries: <strong>{dictCount !== null ? dictCount : "Loading..."}</strong></p>
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
                    <button className="nav-btn" onClick={() => navigate("/dictionary")}>📖 View / Edit</button>
                    <button className="nav-btn" onClick={() => navigate("/dictionary/import")}>📥 Import CSV</button>
                    <button className="nav-btn" onClick={() => navigate("/dictionary/add")}>➕ Add Entry</button>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem", justifyContent: "center", flexWrap: "wrap" }}>
                    <button className="nav-btn secondary" onClick={handleCheckDuplicates} disabled={dedupLoading}>
                      {dedupLoading ? "Checking…" : "🔍 Check Duplicates"}
                    </button>
                    <button className="nav-btn secondary" onClick={handleCheckInvalid} disabled={invalidLoading}>
                      {invalidLoading ? "Checking…" : "🚫 Check Invalid Entries"}
                    </button>
                  </div>
                  {dedupStats !== null && (
                    <div style={{ marginTop: "0.75rem" }}>
                      {dedupStats.entries_to_remove === 0 ? (
                        <p style={{ color: "#1a7a1a", fontSize: "0.9rem", margin: 0 }}>No duplicates found — dictionary is clean.</p>
                      ) : (
                        <>
                          <p style={{ color: "#856404", fontSize: "0.9rem", margin: "0 0 0.5rem" }}>
                            Found <strong>{dedupStats.duplicate_groups}</strong> duplicate {dedupStats.duplicate_groups === 1 ? "group" : "groups"} —{" "}
                            <strong>{dedupStats.entries_to_remove}</strong> {dedupStats.entries_to_remove === 1 ? "entry" : "entries"} to remove.
                            The record with the most recent book values will be kept.
                          </p>
                          {!dedupConfirming ? (
                            <button className="nav-btn" style={{ background: "#dc3545", borderColor: "#dc3545" }} onClick={() => setDedupConfirming(true)}>
                              🗑️ Remove Duplicates
                            </button>
                          ) : (
                            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", alignItems: "center" }}>
                              <span style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>Are you sure?</span>
                              <button className="nav-btn" style={{ background: "#dc3545", borderColor: "#dc3545" }} onClick={handleDeduplicate} disabled={dedupLoading}>
                                {dedupLoading ? "Removing…" : "Yes, Remove"}
                              </button>
                              <button className="nav-btn secondary" onClick={() => setDedupConfirming(false)}>Cancel</button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {dedupMsg && (
                    <p style={{ marginTop: "0.5rem", fontSize: "0.9rem", color: dedupMsg.startsWith("Dedup failed") || dedupMsg.startsWith("Check failed") ? "#dc3545" : "#1a7a1a" }}>
                      {dedupMsg}
                    </p>
                  )}

                  <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: "0.35rem 0 0" }}>
                    Invalid entries: brand/year combos that couldn't exist (e.g. Score 1952, Upper Deck 1975).
                  </p>
                  {invalidStats !== null && (
                    <div style={{ marginTop: "0.75rem" }}>
                      {invalidStats.total === 0 ? (
                        <p style={{ color: "#1a7a1a", fontSize: "0.9rem", margin: 0 }}>No invalid entries found — dictionary is clean.</p>
                      ) : (
                        <>
                          <p style={{ color: "#856404", fontSize: "0.9rem", margin: "0 0 0.25rem" }}>
                            Found <strong>{invalidStats.total}</strong> invalid {invalidStats.total === 1 ? "entry" : "entries"}:
                            {Object.entries(invalidStats.by_brand).map(([b, n]) => ` ${b} (${n})`).join(",")}
                          </p>
                          {!invalidConfirming ? (
                            <button className="nav-btn" style={{ background: "#dc3545", borderColor: "#dc3545" }} onClick={() => setInvalidConfirming(true)}>
                              🗑️ Purge Invalid Entries
                            </button>
                          ) : (
                            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", alignItems: "center" }}>
                              <span style={{ fontSize: "0.9rem", color: "var(--text-muted)" }}>Are you sure?</span>
                              <button className="nav-btn" style={{ background: "#dc3545", borderColor: "#dc3545" }} onClick={handlePurgeInvalid} disabled={invalidLoading}>
                                {invalidLoading ? "Purging…" : "Yes, Purge"}
                              </button>
                              <button className="nav-btn secondary" onClick={() => setInvalidConfirming(false)}>Cancel</button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}
                  {invalidMsg && (
                    <p style={{ marginTop: "0.5rem", fontSize: "0.9rem", color: invalidMsg.startsWith("Purge failed") || invalidMsg.startsWith("Check failed") ? "#dc3545" : "#1a7a1a" }}>
                      {invalidMsg}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Value Dictionary */}
            <div className="card-section">
              <SectionHeader id="d-value">
                Value Dictionary <InfoIcon id="valuedictionary" text="Admin-maintained book values (High → Low) keyed on brand + year + card number. Smart Fill auto-populates these when adding cards." />
              </SectionHeader>
              {openSections.has("d-value") && (
                <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
                  <p>
                    Entries with values: <strong>{valuesStats ? valuesStats.values_count : "Loading..."}</strong>
                    {valuesStats?.last_imported_at && (
                      <span style={{ marginLeft: "1rem", fontSize: "0.85rem", color: "var(--text-muted)" }}>
                        Last import: {new Date(valuesStats.last_imported_at).toLocaleDateString()}
                      </span>
                    )}
                  </p>
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", justifyContent: "center", flexWrap: "wrap" }}>
                    <button className="nav-btn" onClick={() => navigate("/dictionary/import-values")}>📥 Import Values CSV</button>
                    <button className="nav-btn" onClick={handleSeedFromCards} disabled={seedLoading}>
                      {seedLoading ? "Seeding..." : "🌱 Seed from My Cards"}
                    </button>
                  </div>
                  {seedMsg && (
                    <p style={{ marginTop: "0.5rem", fontSize: "0.9rem", color: seedMsg.startsWith("Seed failed") ? "#dc3545" : "#1a7a1a" }}>
                      {seedMsg}
                    </p>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════
            TAB: Data  (3 accordion sections)
        ══════════════════════════════════════════ */}
        {activeTab === "data" && (
          <>
            {/* Card Sets — SP+ only */}
            {tierAtLeast("sp") && <div className="card-section" style={{ marginBottom: "1rem" }}>
              <SectionHeader id="data-sets">Card Sets <InfoIcon id="cardsets" text="Control which set checklists appear in the Builds page. Import new sets or toggle visibility by brand and year." /></SectionHeader>
              {openSections.has("data-sets") && (
                <div style={{ marginTop: "0.75rem" }}>
                  <p style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginBottom: "0.75rem", textAlign: "center" }}>
                    Import global set master lists. Users can then track completion card-by-card.
                  </p>
                  <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.25rem", justifyContent: "center" }}>
                    <button className="nav-btn" onClick={() => navigate("/builds")}>🏗️ View Builds</button>
                    <button className="nav-btn" onClick={() => navigate("/builds/import")}>📥 Import Set CSV</button>
                  </div>

                  {allSets.length > 0 && (
                    <div>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.75rem" }}>
                        <span style={{ fontWeight: 600, fontSize: "0.9rem", color: "var(--text-secondary)" }}>Visible Sets</span>
                        <div style={{ display: "flex", gap: "0.4rem" }}>
                          <button type="button"
                            onClick={() => { setSettings(prev => ({ ...prev, visible_set_ids: null })); debouncedSave({ visible_set_ids: null }); }}
                            style={{ fontSize: "0.78rem", padding: "2px 10px", borderRadius: 20, border: "1px solid var(--border)", background: "var(--bg-input)", cursor: "pointer", color: "var(--text-secondary)" }}>
                            Select All</button>
                          <button type="button"
                            onClick={() => { setSettings(prev => ({ ...prev, visible_set_ids: [] })); debouncedSave({ visible_set_ids: [] }); }}
                            style={{ fontSize: "0.78rem", padding: "2px 10px", borderRadius: 20, border: "1px solid var(--border)", background: "var(--bg-input)", cursor: "pointer", color: "var(--text-secondary)" }}>
                            Deselect All</button>
                        </div>
                      </div>
                      <div style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.4rem" }}>Brand</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem", marginBottom: "1rem" }}>
                        {brands.map(brand => {
                          const total = setsByBrand[brand].length;
                          const nVis = visibleCountForBrand(brand);
                          const isActive = activeBrand === brand;
                          const isDark = document.documentElement.dataset.theme === "dark";
                          const countColor = nVis === 0 ? "#dc2626" : nVis === total ? "#16a34a" : "#f59e0b";  // NOSONAR
                          const bgColor = nVis === total ? (isDark ? "#052e16" : "#f0fdf4") : nVis === 0 ? "var(--bg-muted)" : "var(--bg-input)";  // NOSONAR
                          return (
                            <button key={brand} type="button" onClick={() => setActiveBrand(isActive ? null : brand)}
                              style={{
                                border: `2px solid ${isActive ? "#1976d2" : "var(--border)"}`,
                                borderRadius: 20, padding: "0.35rem 0.9rem",
                                background: bgColor, color: nVis === 0 ? "var(--text-muted)" : "var(--text-primary)",
                                cursor: "pointer", fontSize: "0.85rem", fontWeight: isActive ? 700 : 500, transition: "border-color 0.15s",
                              }}>
                              {brand}{" "}<span style={{ color: countColor, fontSize: "0.78rem" }}>{nVis}/{total}</span>
                            </button>
                          );
                        })}
                      </div>
                      {activeBrand && setsByBrand[activeBrand] && (
                        <div style={{ borderTop: "1px solid var(--border)", paddingTop: "0.75rem" }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
                            <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Years — {activeBrand}</span>
                            <div style={{ display: "flex", gap: "0.4rem" }}>
                              <button type="button" onClick={() => selectAllForBrand(activeBrand)}
                                style={{ fontSize: "0.78rem", padding: "2px 10px", borderRadius: 20, border: "1px solid var(--border)", background: "var(--bg-input)", cursor: "pointer", color: "var(--text-secondary)" }}>All</button>
                              <button type="button" onClick={() => clearAllForBrand(activeBrand)}
                                style={{ fontSize: "0.78rem", padding: "2px 10px", borderRadius: 20, border: "1px solid var(--border)", background: "var(--bg-input)", cursor: "pointer", color: "var(--text-secondary)" }}>None</button>
                            </div>
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                            {setsByBrand[activeBrand].map(s => {
                              const vis = isSetVisible(s.id);
                              const isDark = document.documentElement.dataset.theme === "dark";
                              return (
                                <button key={s.id} type="button" onClick={() => toggleYear(s.id)}
                                  style={{
                                    border: `2px solid ${vis ? "#16a34a" : "var(--border)"}`,
                                    borderRadius: 20, padding: "0.25rem 0.65rem",
                                    background: vis ? (isDark ? "#052e16" : "#f0fdf4") : "var(--bg-muted)",  // NOSONAR
                                    color: vis ? "#16a34a" : "var(--text-muted)",
                                    cursor: "pointer", fontSize: "0.85rem", fontWeight: vis ? 600 : 400, transition: "all 0.1s",
                                  }}>
                                  {s.year}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>}

            {/* Card Import */}
            <div className="card-section" style={{ marginBottom: "1rem" }}>
              <SectionHeader id="data-import">
                Card Import <InfoIcon id="cardimport" text="Bulk import cards from a CSV file. See the Import Cards page for template and formatting guide." />
              </SectionHeader>
              {openSections.has("data-import") && (
                <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
                  <p style={{ fontSize: "0.8rem", color: "#777", marginBottom: "0.75rem" }}>Bulk import cards from a CSV file</p>
                  <button className="nav-btn" onClick={() => navigate("/import-cards")}>📥 Import Cards</button>
                </div>
              )}
            </div>

            {/* Data Management */}
            <div className="card-section">
              <SectionHeader id="data-mgmt">
                Data Management <InfoIcon id="datamanagement" text="Tools for extracting, backing up, and restoring your collection data." />
              </SectionHeader>
              {openSections.has("data-mgmt") && (
                <div style={{ marginTop: "0.75rem", textAlign: "center" }}>
                  {/* Extract */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <div style={{ fontWeight: 600, fontSize: "1rem", marginBottom: "0.25rem" }}>
                      Extract Card Data <InfoIcon id="extract" text="Download your card data in CSV, TSV, or JSON format for use in other tools" />
                    </div>
                    <p style={{ fontSize: "0.8rem", color: "#777", marginBottom: "0.75rem" }}>Card data only — open in Excel, Google Sheets, or any tool</p>
                    <div style={{ display: "flex", justifyContent: "center", gap: "1.5rem", marginBottom: "0.75rem" }}>
                      {["csv", "tsv", "json"].map(fmt => (
                        <label key={fmt} style={{ display: "flex", alignItems: "center", gap: "0.35rem", cursor: "pointer" }}>
                          <input type="radio" name="exportFormat" value={fmt} checked={exportFormat === fmt} onChange={e => setExportFormat(e.target.value)} />
                          {fmt.toUpperCase()}
                        </label>
                      ))}
                    </div>
                    <button className="nav-btn" onClick={handleExport} disabled={dmLoading !== null} style={{ opacity: dmLoading !== null ? 0.65 : 1 }}>
                      {dmLoading === "export" ? "Downloading..." : "Download"}
                    </button>
                  </div>

                  <hr style={{ border: "none", borderTop: "1px solid #e0e0e0", margin: "0 0 1.5rem 0" }} />

                  {/* Backup */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <div style={{ fontWeight: 600, fontSize: "1rem", marginBottom: "0.25rem" }}>
                      Full Backup <InfoIcon id="backup" text="Download cards + settings as a JSON file you can restore from later" />
                    </div>
                    <p style={{ fontSize: "0.8rem", color: "#777", marginBottom: "0.75rem" }}>Cards + settings — use this to restore your collection later</p>
                    <button className="nav-btn" onClick={handleBackup} disabled={dmLoading !== null} style={{ opacity: dmLoading !== null ? 0.65 : 1 }}>
                      {dmLoading === "backup" ? "Downloading..." : "Download Backup"}
                    </button>
                  </div>

                  <hr style={{ border: "none", borderTop: "1px solid #e0e0e0", margin: "0 0 1.5rem 0" }} />

                  {/* Restore */}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "1rem", marginBottom: "0.25rem" }}>
                      Restore from Backup <InfoIcon id="restore" text="Upload a backup file to replace your current collection" />
                    </div>
                    <p style={{ fontSize: "0.8rem", color: "#777", marginBottom: "0.75rem" }}>Replaces all current cards with data from a backup file</p>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
                      <input type="file" accept=".json" onChange={e => { setRestoreFile(e.target.files[0]); setRestoreMsg(""); setRestoreError(""); }} />
                      <button className="nav-btn" onClick={handleRestore} disabled={!restoreFile || dmLoading !== null}
                        style={{ background: "#dc3545", opacity: (!restoreFile || dmLoading !== null) ? 0.65 : 1 }}>
                        {dmLoading === "restore" ? "Restoring..." : "Restore"}
                      </button>
                    </div>
                    {restoreMsg && <p style={{ color: "green", marginTop: "0.75rem" }}>{restoreMsg}</p>}
                    {restoreError && <p style={{ color: "red", marginTop: "0.75rem" }}>{restoreError}</p>}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* ══════════════════════════════════════════
            TAB: Users
        ══════════════════════════════════════════ */}
        {activeTab === "users" && isAdmin && (
          <div data-tour="admin-users" className="card-section" style={{ marginBottom: "1rem" }}>
            <h3 style={{ marginTop: 0, marginBottom: "0.75rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
              User Feature Management
              <InfoIcon id="user-features" text="Toggle features and nav items per user. Changes save immediately. Disabled nav items appear greyed out in the user's nav bar." />
            </h3>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", whiteSpace: "nowrap" }}>
                <thead>
                  <tr>
                    <th colSpan={2}></th>
                    <th colSpan={4} style={{ textAlign: "center", padding: "6px 8px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#1565c0", background: "#e3f2fd", borderBottom: "2px solid #1976d2", borderRight: "1px solid var(--border-light)" }}>Features</th>
                    <th colSpan={8} style={{ textAlign: "center", padding: "6px 8px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: "#1b5e20", background: "#e8f5e9", borderBottom: "2px solid #28a745" }}>Inventory</th>
                  </tr>
                  <tr style={{ borderBottom: "1px solid var(--border)" }}>
                    <th style={{ textAlign: "left", padding: "8px 14px", minWidth: 120 }}>User</th>
                    <th style={{ textAlign: "center", padding: "8px 10px", minWidth: 80, fontWeight: 600, color: "#555" }}>Tier</th>
                    {[
                      ["enable_smart_fill",   "🧠", "Smart Fill"],
                      ["chatbot_enabled",      "💬", "Chatbot"],
                      ["enable_image_ai",      "📷", "Image AI"],
                      ["enable_graded_cards",  "🏆", "Graded"],
                      ["nav_cards",            "📇", "Cards"],
                      ["nav_gallery",          "🎴", "Gallery"],
                      ["nav_balls",            "⚾", "Balls"],
                      ["nav_builds",           "🏗️", "Builds"],
                      ["nav_sets_binders",     "📓", "Sets"],
                      ["nav_wax",              "📦", "Wax"],
                      ["nav_packs",            "🧧", "Packs"],
                      ["nav_analytics",        "📊", "Analytics"],
                    ].map(([key, icon, label], i) => (
                      <th key={key} style={{ textAlign: "center", padding: "8px 8px", color: "#555", fontWeight: 600, minWidth: 64, background: i < 4 ? "#e3f2fd" : "#e8f5e9", borderRight: i === 3 ? "1px solid var(--border-light)" : undefined }} title={label}>
                        <span style={{ fontSize: "1.1rem" }}>{icon}</span>
                        <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", marginTop: 2 }}>{label}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {adminUsers.length === 0 && (
                    <tr><td colSpan={14} style={{ textAlign: "center", padding: "1.5rem", color: "var(--text-muted)" }}>Loading users…</td></tr>
                  )}
                  {adminUsers.map(u => {
                    const navEnabled = (key) => u.nav_items === null || (u.nav_items ?? ALL_NAV_KEYS).includes(key);
                    return (
                      <tr key={u.id} style={{ borderBottom: "1px solid var(--border-light)" }}>
                        <td style={{ padding: "7px 14px", fontWeight: 600 }}>{u.username || u.email}</td>
                        <td style={{ textAlign: "center", padding: "5px 8px" }}>
                          <select value={u.subscription_tier || "base"}
                            onChange={e => handleUserTierChange(u.id, e.target.value)}
                            style={{ fontSize: "0.8rem", padding: "2px 6px", borderRadius: 4,
                              border: `2px solid ${{ base: "#6c757d", rc: "#f97316", sp: "#28a745", elite: "#1976d2" }[u.subscription_tier] ?? "#6c757d"}`,
                              background: "var(--bg-input)", color: "var(--text-primary)", cursor: "pointer", fontWeight: 700 }}>
                            {["base","rc","sp","elite"].map(t => (
                              <option key={t} value={t}>{t.toUpperCase()}</option>
                            ))}
                          </select>
                        </td>
                        {/* Feature flag checkboxes */}
                        {[
                          ["enable_smart_fill",    u.enable_smart_fill,    false],
                          ["chatbot_enabled",      u.chatbot_enabled,      false],
                          ["enable_image_ai",      u.enable_image_ai,      false],
                          ["enable_graded_cards",  u.enable_graded_cards,  true],
                        ].map(([field, val, last]) => (
                          <td key={field} style={{ textAlign: "center", padding: "7px 8px", background: "#f0f8ff", borderRight: last ? "1px solid var(--border-light)" : undefined }}>
                            <input type="checkbox" checked={!!val}
                              onChange={() => handleUserFeatureToggle(u.id, field, !val)} />
                          </td>
                        ))}
                        {/* Nav item checkboxes */}
                        {["cards","gallery","balls","builds","sets_binders","wax","packs","analytics"].map(key => {
                          const enabled = navEnabled(key);
                          const locked = key === "cards";
                          return (
                            <td key={key} style={{ textAlign: "center", padding: "7px 8px", background: "#f1f8f1" }}>
                              <input type="checkbox" checked={enabled} disabled={locked}
                                onChange={() => !locked && handleUserNavToggle(u.id, key, u.nav_items)}
                                style={locked ? { opacity: 0.4, cursor: "not-allowed" } : {}} />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showModal && (
          <div className="modal-overlay" role="button" tabIndex={0}  // NOSONAR
            style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.4)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 9999 }}
            onClick={() => setShowModal(false)}
            onKeyDown={(e) => { if (e.key === "Escape") setShowModal(false); }}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}
              style={{ background: "#f9f9f9", borderRadius: "12px", padding: "1.5rem 2rem", width: "320px", boxShadow: "0 4px 10px rgba(0,0,0,0.2)", textAlign: "center" }}>
              <h3 style={{ marginBottom: "0.75rem", color: "#333" }}>Valuation Complete</h3>
              <p style={{ color: "#444", marginBottom: "1.5rem" }}>{modalMessage}</p>
              <button onClick={() => setShowModal(false)}
                style={{ backgroundColor: "#007bff", color: "white", border: "none", borderRadius: "6px", padding: "0.4rem 1rem", cursor: "pointer" }}>
                OK
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
